import React, { useState, useEffect, createContext, useRef } from 'react';
import { CircularProgress } from '@mui/material';
import NavigationBar from '../components/NavigationBar';
import { logTabNavigation } from '../logger';
import ClientsContent from '../components/Clients/ClientsContent';
import styles from './Dashboard.module.scss';
import EmptyState from '../components/composites/EmptyState';
import { NavigationRoutes } from '../constants/navigationConsts';
import TrainerExercisesContent from '../components/Exercises/TrainerExercisesContent';
import Programs from '../components/Programs/Programs';
import RoutineTemplates from '../components/Workouts/RoutineTemplates';
import Trainers from '../components/Trainers/Trainers';
import Groups from '../components/Groups/Groups';
import LiveWorkouts from '../components/LiveWorkouts/LiveWorkouts';
import LiveDemo from '../components/LiveWorkouts/LiveDemo';
import GymSettings from '../components/GymSettings/GymSettings';
import Managers from '../components/Managers/Managers';
import Gyms from '../components/Gyms/Gyms';
import StationsHealth from '../components/StationHealth/StationsHealth';
import { isHighSchool } from '../utils/dashboardUtils';
import {
  useSessionAction,
  useSessionData,
  useUnsavedChanges,
} from '../providers';
import TrainerTermsDialog from '../components/Trainers/TrainerTermsDialog';

// TODO: Remove when live workouts are implemented
// import { GYM_IDS_TO_SHOW_MOCK } from '../mock/liveWorkouts';
import { MOTIVISION_ADMINS } from '../constants';

import type { Manager, Trainer } from '../types';

export const DashboardScrollContext = createContext<{
  scrollToTop: () => void;
}>({
  scrollToTop: () => {},
});

const getManagersRoutes = (): NavigationRoutes[] => {
  return [
    NavigationRoutes.Clients,
    NavigationRoutes.Managers,
    NavigationRoutes.Trainers,
  ];
};

const getTrainersRoutes = (enableLiveWorkouts: boolean): NavigationRoutes[] => {
  const baseRoutes: NavigationRoutes[] = [
    NavigationRoutes.Clients,
    NavigationRoutes.Programs,
    NavigationRoutes.Workouts,
    NavigationRoutes.Exercises,
    NavigationRoutes.Groups,
  ];
  if (enableLiveWorkouts) {
    baseRoutes.push(NavigationRoutes.LiveWorkouts);
  }
  return baseRoutes;
};

// const getDemoRoutes = (selectedGymId: string): NavigationRoutes[] => {
//   if (GYM_IDS_TO_SHOW_MOCK.includes(selectedGymId)) {
//     return [NavigationRoutes.LiveWorkouts];
//   }
//   return [];
// };

const getAdminRoutes = (managerId: string): NavigationRoutes[] => {
  if (MOTIVISION_ADMINS.includes(managerId)) {
    return [
      NavigationRoutes.LiveDemo,
      NavigationRoutes.GymSettings,
      NavigationRoutes.Gyms,
      NavigationRoutes.StationsHealth,
    ];
  }
  return [];
};

const getRouteTitle = (
  navigationRoute: NavigationRoutes,
  isHighSchool: boolean,
): string => {
  switch (navigationRoute) {
    case NavigationRoutes.Clients:
      return isHighSchool ? 'Athletes' : 'Clients';
    default:
      return navigationRoute;
  }
};

const getRoutes = (
  selectedGymId: string,
  manager: Manager | undefined,
  trainer: Trainer | undefined,
  isHighSchool: boolean,
  enableLiveWorkouts: boolean,
): Array<{ navigation: NavigationRoutes; title: string }> => {
  const managerRoutes = manager ? getManagersRoutes() : [];
  const trainerRoutes = trainer ? getTrainersRoutes(enableLiveWorkouts) : [];
  // const demoRoutes = getDemoRoutes(selectedGymId);
  const adminRoutes = getAdminRoutes(manager?.id || '');
  const combinedRoutes = new Set([
    ...managerRoutes,
    ...trainerRoutes,
    // ...demoRoutes,
    ...adminRoutes,
  ]);
  return Array.from(combinedRoutes).map((navigation) => ({
    navigation,
    title: getRouteTitle(navigation, isHighSchool),
  }));
};

const ANIMATION_DURATION = 100;

type Direction = 'up' | 'down';
type TransitionStage = 'idle' | 'exit' | 'enter';

const Dashboard: React.FC = () => {
  const { loginTrainer, loginManager, gym } = useSessionData();
  const { confirmExitIfNeeded } = useUnsavedChanges();
  const { isImpersonating } = useSessionAction();
  const mainContentRef = useRef<HTMLDivElement>(null);
  const navigationRoutes = getRoutes(
    gym?.id || '',
    loginManager || undefined,
    loginTrainer || undefined,
    isHighSchool(gym),
    Boolean(gym?.configuration?.enabledFeatures?.enableDashboardLiveWorkouts),
  );
  const [navigationRoute, setNavigationRoute] =
    useState<NavigationRoutes | null>(navigationRoutes[0]?.navigation || null);
  const [showTrainerTermsDialog, setShowTrainerTermsDialog] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState<boolean>(false);
  const [transitionStage, setTransitionStage] =
    useState<TransitionStage>('idle');
  const [pendingRoute, setPendingRoute] = useState<NavigationRoutes | null>(
    null,
  );
  const [direction, setDirection] = useState<Direction>('down');

  const scrollToTop = () => {
    if (mainContentRef.current) {
      mainContentRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  useEffect(() => {
    if (loginTrainer && !loginTrainer.preferences?.consentToTerms) {
      setShowTrainerTermsDialog(true);
    }
  }, [loginTrainer]);

  const getRouteIndex = (route: NavigationRoutes | null) =>
    navigationRoutes.findIndex((r) => r.navigation === route);

  useEffect(() => {
    if (!navigationRoutes.length) {
      setNavigationRoute(null);
      return;
    }
    if (
      navigationRoute &&
      !navigationRoutes.some((r) => r.navigation === navigationRoute)
    ) {
      setNavigationRoute(navigationRoutes[0].navigation);
    } else if (!navigationRoute) {
      setNavigationRoute(navigationRoutes[0].navigation);
    }
  }, [navigationRoutes, navigationRoute]);

  useEffect(() => {
    if (transitionStage === 'exit') {
      const timeout = window.setTimeout(() => {
        if (pendingRoute) {
          setNavigationRoute(pendingRoute);
          setTransitionStage('enter');
        } else {
          setTransitionStage('idle');
        }
      }, ANIMATION_DURATION);
      return () => window.clearTimeout(timeout);
    }
    if (transitionStage === 'enter') {
      const timeout = window.setTimeout(() => {
        setTransitionStage('idle');
        setPendingRoute(null);
      }, ANIMATION_DURATION);
      return () => window.clearTimeout(timeout);
    }
    return;
  }, [transitionStage, pendingRoute]);

  const startRouteTransition = (route: NavigationRoutes) => {
    const currentIdx = getRouteIndex(navigationRoute);
    const nextIdx = getRouteIndex(route);
    setDirection(nextIdx > currentIdx ? 'up' : 'down');
    setPendingRoute(route);
    setTransitionStage('exit');
  };

  const handleTabSwitch = (route: NavigationRoutes) => {
    if (transitionStage !== 'idle' || route === navigationRoute) {
      return;
    }
    logTabNavigation(route);
    confirmExitIfNeeded(() => startRouteTransition(route));
  };

  const getMainContent = (route: NavigationRoutes | null) => {
    switch (route) {
      case NavigationRoutes.LiveWorkouts:
        return <LiveWorkouts />;
      case NavigationRoutes.LiveDemo:
        return <LiveDemo />;
      case NavigationRoutes.Clients:
        return <ClientsContent />;
      case NavigationRoutes.Exercises:
        return <TrainerExercisesContent />;
      case NavigationRoutes.Workouts:
        return <RoutineTemplates />;
      case NavigationRoutes.Programs:
        return <Programs />;
      case NavigationRoutes.Groups:
        return <Groups />;
      case NavigationRoutes.Trainers:
        return <Trainers />;
      case NavigationRoutes.Managers:
        return <Managers />;
      case NavigationRoutes.GymSettings:
        return <GymSettings />;
      case NavigationRoutes.Gyms:
        return <Gyms />;
      case NavigationRoutes.StationsHealth:
        return <StationsHealth />;
      default:
        return null;
    }
  };

  if (!gym) {
    if (isImpersonating) {
      return (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '64px 16px',
            gap: 16,
          }}
        >
          <CircularProgress />
          <div style={{ textAlign: 'center' }}>
            Switching accounts… Loading the new dashboard.
          </div>
        </div>
      );
    }
    return (
      <EmptyState
        text="It looks like you don't have a gym yet. Please contact your gym
        administrator for help."
      />
    );
  }

  const handleTrainerTermsConsent = async () => {
    setShowTrainerTermsDialog(false);
  };

  const handleTrainerTermsDecline = async () => {
    setShowTrainerTermsDialog(false);
  };

  return (
    <DashboardScrollContext.Provider value={{ scrollToTop }}>
      <div className={styles.dashboard}>
        {loginTrainer && (
          <TrainerTermsDialog
            trainer={loginTrainer}
            visible={showTrainerTermsDialog}
            onConsent={handleTrainerTermsConsent}
            onDecline={handleTrainerTermsDecline}
          />
        )}
        <NavigationBar
          navigationRoutes={navigationRoutes}
          navigationRoute={navigationRoute}
          setNavigationRoute={handleTabSwitch}
          collapsed={sidebarCollapsed}
          onToggleCollapse={() => setSidebarCollapsed((c) => !c)}
        />
        <div ref={mainContentRef} className={styles.mainContent}>
          {/* Current view */}
          <div
            className={`${styles.contentView} ${
              transitionStage === 'exit'
                ? direction === 'up'
                  ? styles.slideOutUp
                  : styles.slideOutDown
                : transitionStage === 'enter'
                  ? direction === 'up'
                    ? styles.slideInUp
                    : styles.slideInDown
                  : ''
            }`}
          >
            {getMainContent(navigationRoute)}
          </div>
        </div>
      </div>
    </DashboardScrollContext.Provider>
  );
};

export default Dashboard;
